import cn from 'classnames';
import {isEmpty} from 'lodash';
import {useIntl} from 'react-intl';
import Card from '@material-ui/core/Card';
import ListIcon from '@material-ui/icons/List';
import GridIcon from '@material-ui/icons/Apps';
import SearchIcon from '@material-ui/icons/Search';
import CardContent from '@material-ui/core/CardContent';
import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import CardActionArea from '@material-ui/core/CardActionArea';
import {GridViewer as ThumbViewer} from '../../DataViewer/GridViewer';
import {Paper, Toolbar, IconButton, Box, TextFieldProps} from '@material-ui/core';
import React, {
  useRef,
  useMemo,
  useState,
  useEffect,
  useReducer,
  useContext,
  useCallback,
} from 'react';

import {getOriginUrl} from '../helpers';
import ImageListFilter from './ImageListFilter';
import {PageListContext} from './PageListProvider';
import {DATASET_SUB_OP as OP} from '../../../config/url';
import {THUMBNAIL_SIZE_GRID, THUMBNAIL_SIZE_LIST} from './const';
import {StudioSearch} from '../../../base-components/StudioSearch';
import {ImageType} from '../../../types/dataset/DatasetGetAnnotationsResponse';
import {StudioImageThumbnail} from '../../../base-components/StudioImageThumbnail';
import {DataPreparationOrigin} from '../../../types/dataset/DataPreparationContainer';
import {useResponsivePageSize} from '../../../base-components/StudioResponsivePageSize/StudioResponsivePageSize';

import './ImageThumbView.scss';

export type ImageStateId = 'reviewed' | 'notReviewed';
export type FilterId = 'sortBy' | 'viewBy';
export type SortId =
  | 'recentlyAddedAsc'
  | 'recentlyAddedDesc'
  | 'recentlyAdded'
  | 'default';
export type ViewId = 'grid' | 'list';

export type ImageDataType = {
  isNew: boolean;
  isReviewed: boolean;
  dateModified?: Date | null;
  data: ImageType;
  annotationCount?: number;
};

type ReducerState = {
  searchText: string;
  viewBy: ViewId;
  sortBy: SortId;
  imageStateBy: ImageStateId[];
};

const initialState: ReducerState = {
  searchText: '',
  viewBy: 'grid',
  sortBy: 'default',
  imageStateBy: [],
};

const GRID_DIMENSION = {
  width: 93,
  height: 93,
  marginX: 16,
  marginY: 17,
};
const LIST_DIMENSION = {
  height: 37,
  marginY: 3,
};

const viewSlice = createSlice({
  name: 'dataset/detection/list',
  initialState,
  reducers: {
    setSearchText(state, action: PayloadAction<string>) {
      state.searchText = action.payload;
    },
    setViewBy(state, action: PayloadAction<ViewId>) {
      state.viewBy = action.payload;
    },
    setSortBy(state, action: PayloadAction<SortId>) {
      state.sortBy = action.payload;
    },
    setImageStateBy(state, action: PayloadAction<ImageStateId[]>) {
      state.imageStateBy = action.payload;
    },
    resetFilterState(state) {
      state.sortBy = initialState.sortBy;
      state.imageStateBy = [];
    },
  },
});

export type ImageThumbViewProps = {
  projectId: string;
  images?: ImageType[];
  selection?: ImageType | null;
  origin: DataPreparationOrigin;
  onClick(image: ImageType): void;
  getImageAnnotationsQuantity(imageId: number): number;
};

export const ImageThumbView = ({
  origin,
  images,
  selection,
  projectId,
  onClick: onParentClick,
  getImageAnnotationsQuantity,
}: ImageThumbViewProps) => {
  const [state, dispatch] = useReducer(viewSlice.reducer, initialState);
  const {viewBy, sortBy, imageStateBy, searchText} = state;
  const {
    setViewBy,
    setSortBy,
    setSearchText,
    setImageStateBy,
    resetFilterState,
  } = viewSlice.actions;
  const [imageData, setImageData] = useState<ImageDataType[] | undefined>([]);
  const listAreaRef = useRef<HTMLDivElement>(null);
  const listCount = useResponsivePageSize({
    mode: viewBy,
    containerRef: listAreaRef,
    gridDimension: GRID_DIMENSION,
    listDimension: LIST_DIMENSION,
  });
  const {
    page,
    pageCount,
    sortedList,
    listContent,
    totalListCount,
    listCount: contextListCount,
    setPageCount,
    setListCount,
    setSortedList,
    setListContent,
    setItemsPerPage,
    setTotalListCount,
  } = useContext(PageListContext);

  useEffect(() => {
    setItemsPerPage(listCount || 0);
  }, [listCount, setItemsPerPage]);

  useEffect(() => {
    const listCount = images?.length || 0;

    if (!isEmpty(images)) {
      const imageDataArr = images?.map(image => ({
        isNew: true,
        data: image,
        annotationCount: getImageAnnotationsQuantity(image.id),
        dateModified: image.date_captured ? new Date(image.date_captured) : null,
        isReviewed: !!imageData?.find(val => val.data === image && val.isReviewed),
      }));

      if (JSON.stringify(imageDataArr) !== JSON.stringify(imageData)) {
        setImageData(imageDataArr);
      }
    }

    if (totalListCount !== listCount) {
      setTotalListCount(listCount);
    }
  }, [images, imageData, totalListCount, setTotalListCount, getImageAnnotationsQuantity]);

  useEffect(() => {
    if (sortedList) {
      const itemCount = listCount || 0;
      const startIndex = (page - 1) * itemCount;
      const fList = sortedList?.slice(startIndex, startIndex + itemCount) || [];
      const newListCount = sortedList?.length || 0;
      const newPageCount = Math.ceil((sortedList?.length || 0) / itemCount);

      if (contextListCount !== newListCount) {
        setListCount(newListCount);
      }

      if (pageCount !== newPageCount) {
        setPageCount(newPageCount);
      }

      if (JSON.stringify(listContent) !== JSON.stringify(fList)) {
        setListContent(fList);
      }
    }
  }, [
    page,
    listCount,
    pageCount,
    sortedList,
    listContent,
    contextListCount,
    setListCount,
    setPageCount,
    setListContent,
  ]);

  const filterImages = useCallback(
    (el: ImageDataType) => {
      let imageStateOk = !imageStateBy.length;
      let {file_name} = el?.data;
      let searchOk = file_name?.toLowerCase().startsWith(searchText.toLowerCase());
      let i = 0;

      while (!imageStateOk && i < imageStateBy.length) {
        imageStateOk =
          (el.isReviewed && imageStateBy.includes('reviewed')) ||
          (!el.isReviewed && imageStateBy.includes('notReviewed'));
        i++;
      }

      return imageStateOk && searchOk;
    },
    [imageStateBy, searchText]
  );

  const onFilterChange = useCallback(
    (groupId: string, id: string, value: string | string[]) => {
      if (groupId === 'sortBy') {
        dispatch(setSortBy(value as SortId));
      }

      if (groupId === 'imageStateBy') {
        dispatch(setImageStateBy(value as ImageStateId[]));
      }
    },
    [dispatch, setImageStateBy, setSortBy]
  );

  const onClick = useCallback(
    (image: ImageDataType) => {
      image.isReviewed = true;

      setImageData(imageData);
      onParentClick(image.data);
    },
    [imageData, onParentClick]
  );

  const sortByRecentlyAdded = useCallback(
    (imageA: ImageDataType, imageB: ImageDataType) => {
      return imageA.isNew === imageB.isNew ? 0 : imageA.isNew ? -1 : 1;
    },
    []
  );

  const sortByRecentlyAddedDesc = useCallback(
    (imageA: ImageDataType, imageB: ImageDataType) => {
      const dateA = imageA?.dateModified?.getTime();
      const dateB = imageB?.dateModified?.getTime();

      return dateA && dateB ? dateB - dateA : 0;
    },
    []
  );

  const sortByRecentlyAddedAsc = useCallback(
    (imageA: ImageDataType, imageB: ImageDataType) => {
      const dateA = imageA?.dateModified?.getTime();
      const dateB = imageB?.dateModified?.getTime();

      return dateA && dateB ? dateA - dateB : 0;
    },
    []
  );

  const sortByDefault = useCallback((imageA: ImageDataType, imageB: ImageDataType) => {
    const imageIdA = imageA?.data?.id;
    const imageIdB = imageB?.data?.id;

    return imageIdA && imageIdB ? imageIdA - imageIdB : 0;
  }, []);

  const getSortFunc = useCallback(() => {
    switch (sortBy) {
      case 'recentlyAdded':
        return sortByRecentlyAdded;
      case 'recentlyAddedDesc':
        return sortByRecentlyAddedDesc;
      case 'recentlyAddedAsc':
        return sortByRecentlyAddedAsc;
      case 'default':
        return sortByDefault;
    }
  }, [
    sortBy,
    sortByDefault,
    sortByRecentlyAdded,
    sortByRecentlyAddedAsc,
    sortByRecentlyAddedDesc,
  ]);

  useEffect(
    function filterAndSortImagesList() {
      if (imageData) {
        const listing = imageData?.filter(filterImages).sort(getSortFunc());

        if (JSON.stringify(listing) !== JSON.stringify(sortedList)) {
          setSortedList(listing);
        }
      }
    },
    [imageData, sortedList, getSortFunc, setSortedList, filterImages]
  );

  const getUrl = useCallback(
    (fileName: string | undefined | null, ids: string, size: number) => {
      const urlMethod = getOriginUrl(origin);

      return `${urlMethod(projectId, OP.GET_BY_ID)}?id=${ids}&resolution=${size}:${size}`;
    },
    [origin, projectId]
  );

  const ids = useMemo(() => listContent?.map(el => el.data.id).join() || '', [
    listContent,
  ]);

  return (
    <Paper className="segmentation-view__thumbnail_container" elevation={0}>
      <Toolbar className="segmentation-view__thumbnail_toolbar" variant="dense">
        <Box flexGrow={1}></Box>
        <SearchField
          value={searchText}
          onChange={e => dispatch(setSearchText(e.target.value))}
        />
        <IconButton
          size="small"
          onClick={() => {
            dispatch(setViewBy(viewBy === 'grid' ? 'list' : 'grid'));
          }}
        >
          {viewBy === 'grid' ? <GridIcon /> : <ListIcon />}
        </IconButton>
        <ImageListFilter
          sortBy={sortBy}
          imageStateBy={imageStateBy}
          onChange={onFilterChange}
          onReset={() => dispatch(resetFilterState())}
        />
      </Toolbar>
      <div className="segmentation-view__thumbnail_list_container" ref={listAreaRef}>
        <ThumbViewer className="segmentation-view__content_thumbnail">
          {!listContent?.length ? (
            <h5 className="segmentation-view__thumbnail_no_result"> No result</h5>
          ) : (
            listContent.map((el, i: number) => {
              return viewBy === 'grid' ? (
                <ThumbnailGridItem
                  key={i}
                  id={String(i)}
                  onClick={() => onClick(el)}
                  selected={selection ? el.data.id === selection.id : false}
                >
                  <StudioImageThumbnail
                    width={THUMBNAIL_SIZE_GRID}
                    height={THUMBNAIL_SIZE_GRID}
                    src={getUrl(el?.data.file_name, ids, THUMBNAIL_SIZE_GRID)}
                    position={i}
                  />
                  {!el.annotationCount ? null : (
                    <div className="segmentation-view__thumbnail_annotation_count">
                      {' '}
                      {el.annotationCount}
                    </div>
                  )}
                </ThumbnailGridItem>
              ) : (
                <ThumbnailListItem
                  key={i}
                  id={String(i)}
                  onClick={() => onClick(el)}
                  selected={el.data === selection}
                  label={el.data.file_name}
                >
                  <StudioImageThumbnail
                    width={THUMBNAIL_SIZE_LIST}
                    height={THUMBNAIL_SIZE_LIST}
                    src={getUrl(el?.data.file_name, ids, THUMBNAIL_SIZE_LIST)}
                    position={i}
                  />
                </ThumbnailListItem>
              );
            })
          )}
        </ThumbViewer>
      </div>
    </Paper>
  );
};

type GridThumbnailProps = {
  id: string;
  label?: string;
  children: React.ReactNode;
  selected?: boolean;
  onClick: (id: string) => void;
};

export const ThumbnailGridItem = ({
  children,
  selected,
  onClick,
  id,
}: GridThumbnailProps) => (
  <Card
    variant="outlined"
    className={cn('segmentation-view__thumbnail', {
      selected: selected,
    })}
  >
    <CardActionArea
      className="segmentation-view__thumbnail_action_area"
      onClick={() => onClick(id)}
    >
      <CardContent className="segmentation-view__thumbnail_content">
        {children}
      </CardContent>
    </CardActionArea>
  </Card>
);

export const ThumbnailListItem = ({
  children,
  selected,
  onClick,
  id,
  label,
}: GridThumbnailProps) => (
  <Card
    variant="outlined"
    className={cn('segmentation-view__thumbnail_list', {
      selected: selected,
    })}
  >
    <CardActionArea
      className="segmentation-view__thumbnail_action_area"
      onClick={() => onClick(id)}
    >
      <CardContent className="segmentation-view__thumbnail_list_content">
        <div className="segmentation-view__thumbnail_list_image">{children}</div>
        <h5>{label}</h5>
      </CardContent>
    </CardActionArea>
  </Card>
);

const SearchField = (props: {value: string; onChange: TextFieldProps['onChange']}) => {
  const [isSearch, setIsSearch] = useState(false);
  const intl = useIntl();

  return (
    <div className="segmentation-view__thumbnail_search_input">
      {isSearch ? (
        <StudioSearch
          autoFocus
          size="small"
          placeholder={intl.formatMessage({id: 'form.search'})}
          onChange={props.onChange}
          value={props.value}
          onBlur={() => {
            !props.value.trim() && setIsSearch(false);
          }}
        />
      ) : (
        <IconButton size="small" onClick={e => setIsSearch(true)}>
          <SearchIcon />
        </IconButton>
      )}
    </div>
  );
};
